import { CaretUp } from 'assets/icons';
import CustomLink from 'components/shared/CustomLink';
import { SDD_NDD_BADGE_ENABLED } from 'utils/featureFlags';

/**
 * COD is not offered in India; every other region keeps this answer.
 * Filtered out by getRingFaqData(isIndia) below.
 */
const codFaqItem = {
  title: 'Is Cash on Delivery (COD) available?',
  description: (
    <>
      Yes, Cash on Delivery (COD) is available. Please note that if your order
      includes a sizing kit, the payment will be collected when the sizing kit
      is delivered.
    </>
  ),
};

export const ringFaqData = [
  {
    title: 'How do I choose the right ring size for me?',
    description: (
      <>
        If you&apos;re uncertain about your Ultrahuman Ring AIR size, we
        recommend opting for the free sizing kit that we&apos;ll promptly
        dispatch it upon placing a Ring order. The sizing kit includes
        high-quality plastic ring sizers ranging from sizes 5 to 14. Please note
        that Ultrahuman Ring AIR sizes may differ slightly from standard US ring
        sizes, and we don&apos;t offer half sizes. If you are already acquainted
        with your Ultrahuman Ring (R1) size, you&apos;ll find the Ring AIR will
        fit you just the same.
      </>
    ),
  },
  {
    title: 'What sizes of the Ring AIR are available?',
    description: (
      <>
        Ultrahuman Ring AIR is available in 10 sizes ranging from the lowest
        size of 5 to the highest size of 14.
      </>
    ),
  },
  {
    title: 'Which finger is optimal for wearing the ring?',
    description: (
      <>
        We suggest wearing the ring on your index, middle, or ring finger. The
        ultimate choice among these depends on which finger provides the most
        comfortable fit or aligns with your personal preference.{' '}
        <CustomLink
          href="https://www.notion.so/ultrahumanapp/Ring-sizing-kit-22aa9960c9b649dda983ca21bfa5ac34?pvs=4"
          target={'_blank'}
        >
          Here&apos;s a more detailed guide
        </CustomLink>{' '}
        to determining the right ring size.
      </>
    ),
  },
  {
    title:
      'Do the Ring AIR sizes correspond to those of other smart rings or the Ultrahuman Ring (R1)?',
    description: (
      <>
        The sizing of the Ring AIR is unique and does not directly align with
        that of other smart rings. However, we offer a complimentary ring sizing
        kit to help you determine the perfect fit.
        <br />
        <br />
        If you are already acquainted with your Ultrahuman Ring (R1) size,
        you&apos;ll find the Ring AIR will fit you just the same.
      </>
    ),
  },
  {
    title: "I placed my ring order, what's next?",
    description: (
      <>
        Upon placing your order, we will promptly process your request for a
        physical Ring AIR sizing kit if you have chosen to receive one. Once you
        have determined your size and submitted it to us (if you haven&apos;t
        done so at the time of placing the order), you can expect the delivery
        of your Ultrahuman Ring AIR within approximately four to six weeks.
      </>
    ),
  },
  {
    title: 'Is there any subscription fee to access ring data?',
    description: (
      <>
        Rest assured, there are no recurring subscription fees for accessing
        your data. Ring AIR is available for a simple, one-time purchase, paid
        upfront. This purchase gives you lifelong access to both the ring and
        your personal data.
      </>
    ),
  },
  codFaqItem,
  {
    title: 'Guidelines on purchasing the ring from countries other than India',
    description: (
      <>
        Please note that it is your responsibility to verify, with your local
        regulatory or competent authorities, whether the Ultrahuman Ring AIR you
        intend to order or have ordered can be legally imported and used in your
        country of residence. We recommend obtaining confirmation from local
        authorities before placing an order on our website.
        <br />
        <br />
        The Ultrahuman Ring AIR available for purchase on our website is
        compliant with Indian laws and regulations. Ultrahuman disclaims any
        liability for non-compliance with local laws if the Ultrahuman Ring AIR
        and our services are used or imported into any other country.
        <br />
        <br />
        Please be aware that the ring may be subject to taxes, customs duties,
        and destination country fees (Import Fees). You, as the recipient of the
        shipment, are responsible for all Import Fees. Similarly, if you
        purchase the Ultrahuman Ring AIR through a crowdfunding campaign, you
        will be responsible for paying import taxes and value-added tax (VAT)
        based on the shipping destination. Please note that Ultrahuman Ring AIR
        purchased from crowdfunding campaigns, such as Kickstarter, are exempt
        from our standard return and refund policy. However, the product
        warranty, in accordance with applicable laws, remains valid.
      </>
    ),
  },
  {
    title: 'What benefits do UltrahumanX subscribers enjoy?',
    description: (
      <>
        <b>UltrahumanX 1-Year Coverage Plan:</b>
        <ul>
          <li>
            Accidental damage protection: Enjoy coverage for accidental damage
            or knocks, redeemable once during your UltrahumanX coverage period.
          </li>
          <li>
            Priority support: No request is too small. We&apos;re here to
            quickly resolve all your queries.
          </li>
        </ul>
        <br />
        <b>UltrahumanX 2-Year Coverage Plan:</b>
        <ul>
          <li>Includes all 1-Year Coverage benefits</li>
          <li>
            Theft and loss coverage: Your peace of mind is important. We&apos;ll
            cover theft or loss with a free replacement, redeemable once during
            your UltrahumanX coverage period.
          </li>
          <li>
            Weight loss insurance: Achieved your weight loss goals? We&apos;ll
            make resizing easy with a free Ultrahuman Ring AIR replacement,
            redeemable once during your UltrahumanX coverage period.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: 'In what colours is the ring available?',
    description: (
      <>
        The Ultrahuman Ring AIR is available in five colours —{' '}
        <b>Raw Titanium</b>, <b>Aster Black</b>, <b>Matte Grey</b>,{' '}
        <b>Bionic Gold</b> and <b>Space Silver</b>.
      </>
    ),
  },
  {
    title: 'What is the battery life of the ring?',
    description: (
      <>
        When fully charged, the ring is designed to operate for approximately
        4-6 days under standard usage conditions. However, specific factors such
        as battery lifespan, environmental temperature, and frequency of use can
        influence this duration. You can anticipate approximately 500 charging
        cycles before there is any significant decline in battery performance.
        As is common with all battery-powered devices, a gradual reduction in
        battery life can be expected after 1 to 2 years. Please note that
        battery-related replacements are not covered beyond the warranty period.
      </>
    ),
  },
  {
    title: 'Which devices are compatible with the ring?',
    description: (
      <>
        The Ultrahuman Ring AIR is designed to seamlessly interface with iPhones
        operating on iOS 15 or newer, as well as Android devices functioning on
        Android 6 or subsequent versions.
      </>
    ),
  },
  {
    title: 'What is Ultrahuman Ring AIR made of?',
    description: (
      <>
        The Ultrahuman Ring AIR is meticulously crafted from durable titanium
        and further reinforced with a tungsten carbide coating, augmenting its
        resilience for everyday activities.
      </>
    ),
  },
  {
    title: 'How does the ring capture and measure body signals?',
    description: (
      <>
        The Ultrahuman Ring AIR employs an array of sensors, including a
        temperature sensor, a PPG (photoplethysmography) sensor, and a
        motion-sensing IMU (inertial measurement unit). By capturing signals
        from your finger, these sensors enable the ring to collect vital data.
        These signals are then processed using advanced algorithms, developed by
        Ultrahuman, to provide you with profound metabolic insights and a
        comprehensive understanding of your body&apos;s dynamics.
      </>
    ),
  },
  {
    title: 'What is Heart Rate Variability (HRV)?',
    description: (
      <>
        Heart Rate Variability, commonly abbreviated as HRV, refers to the
        extent of fluctuation or variation in your heart rate over a specific
        timeframe. HRV serves as a valuable metric for assessing physical
        well-being, including heart conditions, as well as mental health factors
        such as anxiety and stress levels. Generally, a higher HRV is regarded
        as an indication of better overall health.
      </>
    ),
  },
  {
    title: 'Are there distinct features on the app for Ultrahuman Ring AIR?',
    description: (
      <>
        The Ultrahuman Ring AIR operates seamlessly with the existing
        functionalities within the ring section of the app. Should there be the
        introduction of new features, rest assured they will be made available
        for both the Ultrahuman Ring AIR and the Ultrahuman Ring (R1).
      </>
    ),
  },
  {
    title: 'What specific parameters can the ring monitor?',
    description: (
      <>
        The Ring AIR is capable of tracking a comprehensive range of metrics
        associated with Sleep, Movement, and Recovery. This includes Heart Rate,
        HRV, Body Temperature, Sleep Stages, Movement Frequency, and
        Recovery patterns, among others. Additionally, it offers real-time
        insights designed to guide you towards making healthier daily decisions.
        These metrics are also seamlessly integrated with the broader Ultrahuman
        ecosystem of products, such as the Ultrahuman M1. Blood oxygen (SpO2)
        is in development and will be released once it meets our standard for
        accuracy.
      </>
    ),
  },
  {
    title:
      'How do Ultrahuman Ring AIR and M1 sync to promote metabolic health?',
    description: (
      <>
        The Ultrahuman Ring AIR and Ultrahuman M1 work in synergy to provide
        valuable insights into your metabolic health. By combining glucose data
        from the M1 with data on movement, sleep, and body metrics collected by
        the ring, deep correlations can be established. This helps us identify
        how certain foods impact glucose levels, allowing for proactive
        recommendations on activities or interventions to counter or assist
        these effects. The integration of M1 and Ring data empowers us to offer
        personalized and precise insights tailored to your unique metabolic
        health needs.
      </>
    ),
  },
  {
    title: 'How do Ultrahuman Ring AIR and M1 sync to improve sleep health?',
    description: (
      <>
        By leveraging the combined data on Sleep and Glucose, we can analyze in
        real-time how glucose spikes impact your sleep. For example, if
        higher-than-usual glucose spikes occur after a meal, it may be
        correlated with poor sleep quality. Real-time nudges will help you take
        appropriate actions to assist the situation.{' '}
      </>
    ),
  },
  {
    title: 'What distinguishes the Ring AIR from the Ring (R1)?',
    description: (
      <>
        The Ultrahuman Ring AIR exemplifies an evolution in design and user
        experience. With a svelte profile at only 2.4 mm thick, the AIR offers
        unmatched comfort and wearability. The Ring AIR remains impressively
        lightweight at just 2.4 grams*. It achieves this without compromising on
        the suite of features, accuracy, or battery life that is appreciated in
        the R1 model.
        <br />
        <br />
        <i>
          **The weight specification is provided with reference to well-known
          and globally recognized smart rings currently available on the
          market.*
        </i>
      </>
    ),
  },
  {
    title:
      'How does the Ultrahuman Ring AIR distinguish itself from other consumer wearable devices?',
    description: (
      <>
        Unlike many consumer wearables, the Ultrahuman Ring AIR focuses
        specifically on providing insights into factors that influence our
        body&apos;s metabolism. It goes beyond traditional tracking by measuring
        key aspects such as sleep quality, sleep zones, stress levels (through
        HRV), and activity distribution. The distinctive feature lies in its
        capability to not only collect data but also correlate it with real-time
        nudges and insights, enabling users to make informed decisions for
        enhancing their metabolic health.
      </>
    ),
  },
  {
    title: 'How is the Ultrahuman Ring AIR different from the M1 device?',
    description: (
      <>
        The Ultrahuman M1 is a platform that monitors your metabolic fitness
        with the help of a semi-invasive continue glucose monitor, providing
        valuable insights into how your body processes glucose. On the other
        hand, the Ultrahuman Ring AIR is a non-invasive smart ring that focuses
        on measuring metabolic parameters such as sleep, stress, movement, and
        recovery. While each device can provide insights independently, they
        complement each other seamlessly.{' '}
      </>
    ),
  },
  {
    title: 'How accurate is the ring data?',
    description: (
      <>
        The metrics derived from the ring are meticulously benchmarked against
        industry-leading fitness wearable products available worldwide.{' '}
      </>
    ),
  },
  {
    title: 'Is the ring compatible with other CGMs?',
    description: (
      <>
        At present, the ring is exclusively compatible with the CGMs included in
        the Ultrahuman M1 plan.{' '}
      </>
    ),
  },
  {
    title: 'Can I wear the ring on a daily basis?',
    description: (
      <>
        Unquestionably! The Ultrahuman Ring AIR has been crafted with
        hypoallergenic materials, ensuring its suitability for continuous,
        round-the-clock wear.
      </>
    ),
  },
  {
    title: 'Is it advisable to wear the ring while working out?',
    description: (
      <>
        The Ultrahuman Ring AIR is fortified with a robust Tungsten carbide
        coating, affording it a high degree of resilience against daily wear and
        tear. Despite this, we advise against wearing your ring during intense
        weight training sessions or while handling heavy metal objects. Even
        repeated contact with relatively less hard substances can potentially
        leave a mark on the ring.
      </>
    ),
  },
  {
    title: 'Is the ring suitable for wearing underwater?',
    description: (
      <>
        Indeed! The Ultrahuman Ring AIR boasts a water resistance rating that
        extends up to 100 meters, permitting wear during activities like
        swimming, showering, scuba diving and more. To maintain its optimal
        performance, we advise against keeping the ring submerged for periods
        exceeding 30 minutes continuously.
      </>
    ),
  },
  {
    title: 'Guidelines on purchasing the ring from countries other than India',
    description: (
      <>
        Please note that it is your responsibility to verify, with your local
        regulatory or competent authorities, whether the Ultrahuman Ring AIR you
        intend to order or have ordered can be legally imported and used in your
        country of residence. We recommend obtaining confirmation from local
        authorities before placing an order on our website. The Ultrahuman Ring
        AIR available for purchase on our website is compliant with Indian laws
        and regulations. Ultrahuman disclaims any liability for non-compliance
        with local laws if the Ultrahuman Ring AIR and our services are used or
        imported into any other country. Please be aware that the ring may be
        subject to taxes, customs duties, and destination country fees (Import
        Fees). You, as the recipient of the shipment, are responsible for all
        Import Fees. Similarly, if you purchase the Ultrahuman Ring AIR through
        a crowdfunding campaign, you will be responsible for paying import taxes
        and value-added tax (VAT) based on the shipping destination. Please note
        that Ultrahuman Ring AIR purchased from crowdfunding campaigns, such as
        Kickstarter, are exempt from our standard return and refund policy.
        However, the product warranty, in accordance with applicable laws,
        remains valid.
      </>
    ),
  },
  {
    title: 'What is the calibration duration for the ring?',
    description: (
      <>
        The Ultrahuman Ring AIR typically requires a one-time calibration period
        of up to 7 days. Subsequent calibrations will only be necessary if the
        ring undergoes a hard reset.
      </>
    ),
  },
  {
    title: 'What is the required duration to fully charge the ring?',
    description: (
      <>
        The Ultrahuman Ring AIR requires approximately 180 mins to reach a full
        charge.
      </>
    ),
  },
  {
    title: 'Is it possible to share the ring with other family members?',
    description: (
      <>
        Indeed, the ring can be shared with other family members, given that it
        is reset from the app. Please note that the ring should fit the new user
        properly. After resetting, the ring will require up to 7 days for
        recalibration before accurately tracking the new user&apos;s data.
      </>
    ),
  },
  {
    title: 'Does long-term wear of the ring have adverse effects on the skin?',
    description: (
      <>
        The design and construction of the ring prioritize skin health. Composed
        of hypoallergenic materials, it is perfectly suited for prolonged skin
        contact and ensures a comfortable fit. There is no need for concern
        about allergic reactions resulting from extended wear of the ring.
      </>
    ),
  },
  {
    title: 'What should I do if my ring gets stuck?',
    description: (
      <>
        Finger size can fluctuate during the day due to heat, activity,
        hydration, or food intake. This can make the ring feel tighter and
        harder to remove.
        <br />
        <br />
        If your ring feels stuck, try the following:
        <ul>
          <li>Run your hand under cold water to reduce swelling.</li>
          <li>Apply soap or a lubricant to your finger.</li>
          <li>Gently twist the ring while pulling it off. Do not force it.</li>
          <li>
            Raise your hand above heart level for a few minutes to reduce blood
            flow and swelling.
          </li>
          <li>
            If the tightness followed exercise, heat exposure, or a heavy meal,
            wait and try again later once swelling subsides.
          </li>
        </ul>
        <br />
        <b>If the ring still cannot be removed</b> and if none of the above
        methods work, seek medical assistance.
        <ul>
          <li>Do not attempt to cut the ring yourself.</li>
          <li>
            The ring contains an embedded battery and must be cut safely by a
            medical professional. The battery is located in the protruding
            section on the inside of the ring. The ring must be cut away from
            this area.
          </li>
          <li>
            Once cut, a flat-head tool may be used to gently widen the gap and
            remove the ring. If removal is still difficult, a second cut may be
            made approximately one centimeter to the left or right of the first
            cut, again avoiding the battery.
          </li>
          <li>
            After removal, handle the ring carefully as edges may be sharp, and
            dispose of it safely. Your safety comes first. The ring can always
            be replaced.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: 'Is the ring EMF-safe?',
    description: (
      <>
        The Ultrahuman Ring AIR operates as a low-power device that communicates
        with your smartphone using Bluetooth Low Energy (BLE) technology. It
        establishes a connection with the smartphone only when you open the
        Ultrahuman App or at regular time intervals, unlike Bluetooth headsets,
        earbuds, or smartwatches that maintain constant connectivity and data
        transmission/reception. It&apos;s important to note that Bluetooth
        activity for the Ultrahuman Ring AIR remains below 1% throughout the
        day, resulting in significantly lower EMF radiation compared to other
        Bluetooth wearables. Consequently, the Ultrahuman Ring AIR can be
        regarded as one of the safest wearable devices for fitness tracking in
        terms of EMF exposure.
      </>
    ),
  },
  {
    title: 'Is the ring considered a medical device?',
    description: (
      <>
        The Ultrahuman Ring AIR is not classified as a medical device. It is
        designed solely for the purpose of promoting and facilitating a healthy
        lifestyle. The Ring AIR is not intended for managing, treating, or
        preventing diseases, and it should not be relied upon for making any
        diagnostic, treatment, or dosage decisions. It is not meant to
        substitute professional medical opinions or guidance regarding the
        treatment, diagnosis, prevention, or alleviation of any disease or
        disability.
        <br />
        <br />
        It is always important to consult with a doctor or qualified healthcare
        professional before making any changes to medications, routines, or
        nutrition, especially if you have any existing health conditions or
        concerns. Please be aware that the accuracy of biomarker data may be
        influenced if you have been diagnosed with a medical condition.
        Therefore, it is crucial not to disregard or delay seeking professional
        medical advice or treatment based on information obtained from or
        accessed through the Ultrahuman app.
      </>
    ),
  },
  {
    title:
      'Can I wear the ring alongside medical devices like a pacemaker or ICD?',
    description: (
      <>
        In general, wearing and using the Ultrahuman Ring AIR should not
        interfere with the functionality of medical devices such as pacemakers
        or ICDs. However, we strongly recommend consulting with your physician
        before making a decision. The Ultrahuman Ring AIR operates using a very
        low-power electromagnetic field, minimizing the likelihood of
        interference with these medical devices. If you suspect any interference
        or have concerns, discontinue the use of the ring and seek guidance from
        both your physician and the manufacturer of your medical device for
        specific guidelines and recommendations.
      </>
    ),
  },
  {
    title: 'Is the ring safe from Electrostatic Discharge (ESD)?',
    description: (
      <>
        Absolutely! The Ultrahuman Ring AIR is designed to be ESD-safe, ensuring
        that you will not experience any electrostatic shocks from the ring.
      </>
    ),
  },
  {
    title: 'Is it safe to wear the ring on tattooed fingers?',
    description: (
      <>
        Yes, you can wear the ring on tattooed fingers. However, please note
        that if the tattoo is dark and heavy, it may affect the accuracy of the
        readings obtained from the ring.
      </>
    ),
  },
  {
    title:
      'What safety considerations have been incorporated into the design and construction of the ring?',
    description: (
      <>
        The Ultrahuman team prioritizes the safety and comfort of users in the
        design and engineering of their products. The Ultrahuman Rings have been
        meticulously crafted with user comfort in mind. The inner side of the
        rings features a transparent hypoallergenic plastic-based material that
        comes into contact with your skin, ensuring a comfortable experience.
        Additionally, the outer shell of the ring is constructed from Titanium,
        chosen for its durability and longevity. Titanium provides the necessary
        strength and resistance to deformation.
        <br />
        <br />
        In rare and exceptional circumstances, should the need arise, any
        Ultrahuman Ring can be safely removed using either manual or power ring
        cutters, ensuring the wearer&apos;s well-being.
      </>
    ),
  },
  {
    title: 'How should I dispose of the ring?',
    description: (
      <>
        The Ultrahuman Ring contains an internal battery, which classifies it as
        electronic waste (e-waste). To dispose of the battery responsibly, we
        recommend adhering to the regulations and guidelines outlined by your
        local laws and regulations regarding e-waste disposal.
        <br />
        <br />
        Additionally, the charger accompanying the ring is an electronic device
        and should also be disposed of in accordance with the applicable local
        electronics recycling laws and regulations.
      </>
    ),
  },
  {
    title: 'What is the warranty policy for Ring AIR?',
    description: (
      <>
        The Ultrahuman Ring AIR is covered by a limited warranty that protects
        against defects in materials and workmanship. The warranty period lasts
        for one year from the date of delivery.
      </>
    ),
  },
  {
    title: 'What is the return policy for Ring AIR?',
    description: (
      <>
        Our return policy allows you to return the Ring AIR product within 30
        days of delivery if you&apos;re not completely satisfied. The return
        must contain all original accessories, including the charger in their
        original packaging.
      </>
    ),
  },
  {
    title: 'Will I have to pay for return shipping?',
    description: (
      <>
        Return shipping is free. We will provide you with a shipping label once
        your return request is processed.
      </>
    ),
  },
  {
    title: 'What is the ring cancellation policy?',
    description: (
      <>
        You have the option to cancel your order within 7 days before the sizing
        kit has been shipped to you. In the rare event that none of the ring
        sizes provided in the sizing kit fit you, you can still cancel your
        order within 3 days of receiving the sizing kit.
      </>
    ),
  },
  {
    title: 'Terms of Sale',
    description: (
      <>
        You can find the Terms of Sale for Ring AIR{' '}
        <CustomLink href="/termsOfSale/UltrahumanRing">here</CustomLink>.
      </>
    ),
  },
  {
    title: '',
    description: (
      <>
        We do our best to ensure that our photos are as true to colour as
        possible. However, due to inconsistencies of various monitors, lighting
        sources, digital photography, we cannot guarantee that the colour you
        see on your screen accurately portrays the true colour of the product.
        Screen images are intended as a guide only and should not be regarded as
        absolutely correct.
      </>
    ),
  },
];

/** /ring/faq data — India drops the COD answer, all other regions keep it. */
export const getRingFaqData = (isIndia: boolean) =>
  isIndia
    ? ringFaqData.filter((item) => item.title !== codFaqItem.title)
    : ringFaqData;

export const ringBuyPageFaqData_AE = [
  {
    title: 'How do I choose the right ring size for me?',
    description: (
      <>
        If you&apos;re uncertain about your Ultrahuman Ring AIR size, we
        recommend opting for the free sizing kit that we&apos;ll promptly
        dispatch it upon placing a Ring order. The sizing kit includes
        high-quality plastic ring sizers ranging from sizes 5 to 14. Please note
        that Ultrahuman Ring AIR sizes may differ slightly from standard US ring
        sizes, and we don&apos;t offer half sizes. If you are already acquainted
        with your Ultrahuman Ring (R1) size, you&apos;ll find the Ring AIR will
        fit you just the same.
      </>
    ),
  },
  {
    title: "I placed my ring order, what's next?",
    description: (
      <>
        Upon placing your order, we will promptly process your request for a
        physical Ring AIR sizing kit if you have chosen to receive one. Once you
        have determined your size and submitted it to us (if you haven&apos;t
        done so at the time of placing the order), you can expect the delivery
        of your Ultrahuman Ring AIR within approximately four to six weeks.
      </>
    ),
  },
  {
    title: 'Is there any subscription fee to access ring data?',
    description: (
      <>
        Rest assured, there are no recurring subscription fees for accessing
        your data. Ring AIR is available for a simple, one-time purchase, paid
        upfront. This purchase gives you lifelong access to both the ring and
        your personal data.
      </>
    ),
  },
  {
    title: 'In what colours is the ring available?',
    description: (
      <>
        The Ultrahuman Ring AIR is available in five colours —{' '}
        <b>Raw Titanium</b>, <b>Aster Black</b>, <b>Matte Grey</b>,{' '}
        <b>Bionic Gold</b> and <b>Space Silver</b>.
      </>
    ),
  },
  {
    title: 'Is Cash on Delivery (COD) available?',
    description: (
      <>
        Yes, Cash on Delivery (COD) is available. Please note that if your order
        includes a sizing kit, the payment will be collected when the sizing kit
        is delivered.
      </>
    ),
  },
  {
    title: 'Do I need to return my old ring during trade-in?',
    description: (
      <>
        No, you don&apos;t need to send your old ring back to us. The trade-in
        credit is processed digitally based on your serial number verification.
      </>
    ),
  },
  {
    title: 'What should I do with my old ring after opting for trade-in?',
    description: (
      <>
        We encourage you to recycle it responsibly. Dispose of it as e-waste
        through authorized recycling channels near you.
      </>
    ),
  },
  {
    title: '',
    description: (
      <>
        <CustomLink href="/ring/faq">
          See all FAQs{' '}
          <CaretUp
            style={{ transform: 'rotate(90deg)' }}
            width={14}
            height={14}
          />
        </CustomLink>
      </>
    ),
  },
];

type TFunction = (
  key: string,
  values?: Record<string, string | number>,
) => string;

export const getRingBuyPageFaqData = (t: TFunction) => [
  {
    title: t('ring.faq.data.titleA'),
    description: t('ring.faq.data.contentA'),
  },
  {
    title: t('ring.faq.data.titleB'),
    description: t('ring.faq.data.contentB'),
  },
  {
    title: t('ring.faq.data.titleC'),
    description: t('ring.faq.data.contentC'),
  },
  {
    title: t('ring.faq.data.titleE'),
    description: (
      <>
        {t('ring.faq.data.contentE')}
        <b>{t('ring.faq.data.contentEcolorB')}</b>,{' '}
        <b>{t('ring.faq.data.contentEcolorC')}</b>,{' '}
        <b>{t('ring.faq.data.contentEcolorD')}</b>,{' '}
        <b>{t('ring.faq.data.contentEcolorE')}</b> {t('ring.AND')}{' '}
        <b>{t('ring.faq.data.contentEcolorF')}</b>.
      </>
    ),
  },
  {
    title: t('ring.faq.data.tradeInReturn.title'),
    description: t('ring.faq.data.tradeInReturn.description'),
  },
  {
    title: t('ring.faq.data.tradeInRecycle.title'),
    description: t('ring.faq.data.tradeInRecycle.description'),
  },
  {
    title: '',
    description: (
      <>
        <CustomLink href="/ring/faq">
          {t('ring.faq.seeAll')}{' '}
          <CaretUp
            style={{ transform: 'rotate(90deg)' }}
            width={14}
            height={14}
          />
        </CustomLink>
      </>
    ),
  },
];

/** Ring Pro buy page FAQ: no Brushed Rose Gold question, four colours only, then a See all FAQs link to /ring-pro/faq. */
export const getRingProBuyPageFaqData = (t: TFunction) => [
  {
    title: t('ring-pro:ring-pro.faq.data.titleA'),
    description: t('ring-pro:ring-pro.faq.data.contentA'),
  },
  {
    title: t('ring-pro:ring-pro.faq.data.titleB'),
    description: t('ring-pro:ring-pro.faq.data.contentB'),
  },
  {
    title: t('ring-pro:ring-pro.faq.data.titleC'),
    description: t('ring-pro:ring-pro.faq.data.contentC'),
  },
  {
    title: t('ring-pro:ring-pro.faq.data.titleD'),
    description: (
      <>
        {t('ring-pro:ring-pro.faq.data.contentD')}
        <b>{t('ring-pro:ring-pro.faq.data.contentDcolorA')}</b>,{' '}
        <b>{t('ring-pro:ring-pro.faq.data.contentDcolorB')}</b>,{' '}
        <b>{t('ring-pro:ring-pro.faq.data.contentDcolorC')}</b>{' '}
        {t('ring-pro:ring-pro.faq.data.and')}{' '}
        <b>{t('ring-pro:ring-pro.faq.data.contentDcolorD')}</b>.
      </>
    ),
  },
  {
    title: t('ring-pro:ring-pro.faq.data.titleE'),
    description: t('ring-pro:ring-pro.faq.data.contentE'),
  },
  {
    title: t('ring-pro:ring-pro.faq.data.titleF'),
    description: t('ring-pro:ring-pro.faq.data.contentF'),
  },
  {
    title: '',
    description: (
      <>
        <CustomLink href="/ring-pro/faq">
          {t('ring:ring.faq.seeAll')}{' '}
          <CaretUp
            aria-hidden
            style={{ transform: 'rotate(90deg)' }}
            width={14}
            height={14}
          />
        </CustomLink>
      </>
    ),
  },
];

/**
 * India ring buy page FAQ. 2-hour delivery Qs are gated by SDD_NDD_BADGE_ENABLED;
 * when off, the shared "what's next" answer sits after sizing.
 */
export const getRingBuyPageFaqData_IN = (t: TFunction) => [
  ...(SDD_NDD_BADGE_ENABLED
    ? [
        {
          title: t('ring.faq.data.in.cities.title'),
          description: t('ring.faq.data.in.cities.content'),
        },
        {
          title: t('ring.faq.data.in.cutoff.title'),
          description: t('ring.faq.data.in.cutoff.content'),
        },
        {
          title: t('ring.faq.data.in.missed.title'),
          description: t('ring.faq.data.in.missed.content'),
        },
        {
          title: t('ring.faq.data.in.when.title'),
          description: t('ring.faq.data.in.when.content'),
        },
      ]
    : []),
  {
    title: t('ring.faq.data.titleA'),
    description: t('ring.faq.data.contentA'),
  },
  ...(SDD_NDD_BADGE_ENABLED
    ? []
    : [
        {
          title: t('ring.faq.data.titleB'),
          description: t('ring.faq.data.contentB'),
        },
      ]),
  {
    title: t('ring.faq.data.titleC'),
    description: t('ring.faq.data.contentC'),
  },
  {
    title: t('ring.faq.data.titleE'),
    description: (
      <>
        {t('ring.faq.data.contentE')}
        <b>{t('ring.faq.data.contentEcolorB')}</b>,{' '}
        <b>{t('ring.faq.data.contentEcolorC')}</b>,{' '}
        <b>{t('ring.faq.data.contentEcolorD')}</b>,{' '}
        <b>{t('ring.faq.data.contentEcolorE')}</b> {t('ring.AND')}{' '}
        <b>{t('ring.faq.data.contentEcolorF')}</b>.
      </>
    ),
  },
  {
    title: t('ring.faq.data.tradeInReturn.title'),
    description: t('ring.faq.data.tradeInReturn.description'),
  },
  {
    title: t('ring.faq.data.tradeInRecycle.title'),
    description: t('ring.faq.data.tradeInRecycle.description'),
  },
  {
    title: '',
    description: (
      <>
        <CustomLink href="/ring/faq">
          {t('ring.faq.seeAll')}{' '}
          <CaretUp
            style={{ transform: 'rotate(90deg)' }}
            width={14}
            height={14}
          />
        </CustomLink>
      </>
    ),
  },
];
